import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { StabilityRuleSource } from '../../../database/schema.js';
import { pickActorFromUserActions, pickTimestampFromUserActions, titleCase } from '../../../util/index.js';
import { Status } from '../../../util/schemas.js';
import { zql } from '../../../zero-schema.gen.js';
import { itemsDetail, valueItem } from '../../../util/event-log-items.js';
import { EventLogResources, addEventLog } from '../../util.js';
import { findDrugName, findProfileNames } from '../../utils/log-names.js';
import { updateStabilityRule, UpdateStabilityRuleSchema } from '../../utils/stability-rules.js';
import { replaceMedicationProfileReconstitutionDiluents, replaceMedicationProfileStabilityRules } from './replace.js';
export async function updateDrugCatalogItem(tx, prev, args, userActions) {
    const updatedDrugItem = {
        id: args.id,
        catalogType: args.catalogType,
        code: args.code,
        name: titleCase(args.name),
        atcCode: args.atcCode,
        nationalDrugId: args.nationalId,
        prescribingName: args.prescribingName,
        description: args.description,
        status: Status.enum.active,
        ...userActions,
    };
    const promises = [tx.mutate.drugCatalogItems.update(updatedDrugItem)];
    if (tx.location === 'server') {
        const patches = createPatch(prev, updatedDrugItem);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.drugCatalogItem,
            resourceId: args.id,
            action: 'update',
            patches: patches,
            performedBy: pickActorFromUserActions(userActions),
            performedAt: pickTimestampFromUserActions(userActions),
            status: 'OK',
            details: { id: args.id, name: prev.name },
        }));
    }
    await Promise.all(promises);
    return updatedDrugItem;
}
export async function updateMedicationProfile(tx, args, userActions) {
    if (!args.id)
        return null;
    const prevMedicationProfile = await tx.run(zql.medicationProfiles
        .related('stabilityRules')
        .related('allowedReconstitutionDiluents', reconstitutionDiluents => reconstitutionDiluents)
        .where('id', args.id)
        .one());
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const updatedMedicationProfile = {
        id: args.id,
        drugCatalogItemId: args.drugItemId,
        medicineFormId: args.medicineFormId,
        reconstitutionRequired: Boolean(args.reconstitutionRequired),
        isDrySubstance: Boolean(args.isDrySubstance),
        name: args.name,
        oralQuantityBasis: args.oralQuantityBasis,
        status: args.status,
        updatedAt: timestamp,
        updatedBy: actor,
    };
    const promises = [tx.mutate.medicationProfiles.update(updatedMedicationProfile)];
    if (tx.location === 'server') {
        const patches = createPatch(prevMedicationProfile, updatedMedicationProfile);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfile,
            resourceId: updatedMedicationProfile.id,
            action: 'update',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'OK',
            details: {
                id: updatedMedicationProfile.id,
                name: prevMedicationProfile?.name,
                itemName: await findDrugName(tx, updatedMedicationProfile.drugCatalogItemId),
            },
        }));
    }
    await Promise.all(promises);
    if (prevMedicationProfile) {
        await replaceMedicationProfileStabilityRules(tx, { medicationProfile: prevMedicationProfile, newStabilityRules: args.stabilityRules }, userActions);
        await replaceMedicationProfileReconstitutionDiluents(tx, { medicationProfile: prevMedicationProfile, newReconstitutionDiluentsIds: args.reconstitutionDiluentsIds }, userActions);
    }
}
export async function updateMedicationProfileStabilityRule(tx, args, userActions) {
    if (!args.id)
        return;
    const prev = await tx.run(zql.medicationStabilityRules
        .where('stabilityRuleId', args.id)
        .where('medicationProfileId', args.medicationProfileId)
        .related('stabilityRule')
        .where('deletedAt', 'IS', null)
        .one());
    if (prev?.stabilityRule?.source === StabilityRuleSource.enum.MEDICATION_PROFILE) {
        const itemName = tx.location === 'server' ? (await findProfileNames(tx, args.medicationProfileId)).itemName : undefined;
        await updateStabilityRule(tx, args, userActions, itemName);
    }
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const updatedMedicationStabilityRule = {
        id: prev?.id || uuidv7(),
        stabilityRuleId: args.id,
        medicationProfileId: args.medicationProfileId,
        updatedAt: timestamp,
        updatedBy: actor,
    };
    const promises = [tx.mutate.medicationStabilityRules.upsert(updatedMedicationStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch(prev, updatedMedicationStabilityRule);
        const { itemName, profileName } = await findProfileNames(tx, args.medicationProfileId);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfileStabilityRule,
            resourceId: updatedMedicationStabilityRule.id,
            action: 'update',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'OK',
            details: { itemName, ...itemsDetail(valueItem('profile', profileName)) },
        }));
    }
    await Promise.all(promises);
    return updatedMedicationStabilityRule;
}
//# sourceMappingURL=update.js.map